import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { colors, cycleStatusLabel } from "@/theme/colors";
import {
  listCycles,
  operationalStateLabel,
  preferredAssetUrl,
  type CycleListItem,
} from "@/api/cycles";

export function CyclesList() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["cycles"],
    queryFn: listCycles,
    refetchInterval: 5000, // dev: pick up newly uploaded cycles within 5s
  });

  return (
    <div style={{ maxWidth: 960 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: "var(--space-base)",
        }}
      >
        <h2 style={{ fontSize: "var(--fs-display)", lineHeight: "var(--lh-display)" }}>
          Ciclos
        </h2>
        <Link to="/cycles/new" style={primaryLink}>
          ＋ Novo PDF
        </Link>
      </div>

      {isLoading && <p style={{ color: colors.textMuted }}>Carregando…</p>}
      {error && <p style={{ color: colors.danger }}>Falha ao carregar: {String(error)}</p>}

      {data && data.items.length === 0 && (
        <p style={{ color: colors.textMuted }}>
          Nenhum ciclo ainda. Comece enviando um PDF.
        </p>
      )}

      {data && data.items.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <Th>Ciclo</Th>
              <Th>Nome</Th>
              <Th>Pipeline</Th>
              <Th>Status</Th>
              <Th>Estado</Th>
              <Th align="right">Páginas</Th>
              <Th>Criado em</Th>
              <Th>Artefatos</Th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((c) => (
              <CycleRow key={c.cycleId} cycle={c} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CycleRow({ cycle }: { cycle: CycleListItem }) {
  const completed = cycle.status === "COMPLETED";
  return (
    <tr style={rowStyle}>
      <Td>
        <Link
          to={`/cycles/${cycle.cycleId}/review`}
          className="mono"
          style={{ color: colors.intelligence, textDecoration: "none" }}
        >
          {cycle.cycleId}
        </Link>
      </Td>
      <Td>{cycle.name}</Td>
      <Td>
        <span className="mono" style={{ color: colors.textMuted }}>
          {cycle.pipeline}
        </span>
      </Td>
      <Td>
        <StatusChip status={cycle.status} />
      </Td>
      <Td>
        <span style={{ fontSize: 12, color: colors.textMuted }}>
          {operationalStateLabel(cycle.operationalState)}
        </span>
      </Td>
      <Td align="right" mono>
        {cycle.pageCount}
      </Td>
      <Td mono>{new Date(cycle.createdAt).toLocaleString()}</Td>
      <Td>
        {completed ? (
          <div style={{ display: "flex", gap: 4 }}>
            <ArtifactLink
              href={preferredAssetUrl(cycle.cycleId, "annotated.pdf", cycle.downloads.annotatedPdfUrl)!}
              filename={`${cycle.cycleId}-annotated.pdf`}
              label="PDF"
              tone="primary"
            />
            <ArtifactLink
              href={preferredAssetUrl(cycle.cycleId, "cycle.csv", cycle.downloads.cycleCsvUrl)!}
              filename={`${cycle.cycleId}.csv`}
              label="CSV"
              tone="intelligence"
            />
          </div>
        ) : (
          <span style={{ color: colors.textMuted, fontSize: 11 }}>—</span>
        )}
      </Td>
    </tr>
  );
}

function ArtifactLink({
  href,
  filename,
  label,
  tone,
}: {
  href: string;
  filename: string;
  label: string;
  tone: "primary" | "intelligence";
}) {
  const color = tone === "primary" ? colors.primary : colors.intelligence;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      download={filename}
      title={`Baixar ${label}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        border: `1px solid ${color}`,
        borderRadius: "var(--radius-sm)",
        color,
        textDecoration: "none",
        fontSize: 11,
        fontWeight: 600,
        fontFamily: "var(--font-mono)",
      }}
    >
      ⬇ {label}
    </a>
  );
}


function StatusChip({ status }: { status: string }) {
  const bg =
    status === "COMPLETED"
      ? colors.primary
      : status === "FAILED"
        ? colors.danger
        : status === "READY_FOR_REVIEW" || status === "IN_REVIEW"
          ? colors.intelligence
          : colors.neutral;
  const fg =
    status === "COMPLETED"
      ? colors.onPrimary
      : status === "FAILED"
        ? colors.onDanger
        : status === "READY_FOR_REVIEW" || status === "IN_REVIEW"
          ? colors.onIntelligence
          : colors.onNeutral;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: "var(--radius-pill)",
        background: bg,
        color: fg,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {cycleStatusLabel(status)}
    </span>
  );
}

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  background: colors.panel,
  border: `1px solid ${colors.line}`,
  borderRadius: "var(--radius-md)",
  overflow: "hidden",
  fontSize: "var(--fs-body)",
};

const rowStyle: React.CSSProperties = {
  borderTop: `1px solid ${colors.line}`,
};

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      style={{
        textAlign: align,
        padding: "10px 12px",
        background: colors.panelMuted,
        color: colors.textMuted,
        fontSize: "var(--fs-ui)",
        fontWeight: 600,
        borderBottom: `1px solid ${colors.line}`,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  mono?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: "10px 12px",
        verticalAlign: "middle",
        fontFamily: mono ? "var(--font-mono)" : undefined,
        fontSize: mono ? "var(--fs-ui)" : "var(--fs-body)",
      }}
    >
      {children}
    </td>
  );
}

const primaryLink: React.CSSProperties = {
  background: colors.primary,
  color: colors.onPrimary,
  padding: "8px 14px",
  borderRadius: "var(--radius-sm)",
  fontSize: "var(--fs-body)",
  fontWeight: 600,
  textDecoration: "none",
};
